import React, { Fragment } from 'react';
import { FormattedMessage, FormattedDate, FormattedNumber } from 'react-intl';
import PropTypes from 'prop-types';
import _isDate from 'lodash/isDate';
import _isFunction from 'lodash/isFunction';
import _isNumber from 'lodash/isNumber';
import _isString from 'lodash/isString';

import { intlMessage } from 'constants/propTypes';
import { isFragment } from 'helpers/core';
import { reportWarning } from 'helpers/error-logging';
import { isMessage } from 'helpers/intl-fns';

/*

  `<Message>` wraps the three most-used react-intl components
  (FormattedMessage, FormattedDate, FormattedNumber) and adds
  some additional logic to handle common cases.

  // Preferred usage
    <Message message={{ id, defaultMessage }} values={values} />

    <Message message={message} values={values} />
      --> <span>example text</span>

  // With `as`
    <Message message={message} as="div" className="example" />
      --> <div className="example">message text</div>

    <Message message={message} as={CustomComponent} />
      --> <CustomComponent>example text</CustomComponent>

    <Message message={message} as="text" />
      --> <Fragment>example text</Fragment>
      --> example text

  // With render prop
    <Message message={message}>
      {(...chunks) => <div>{chunks}</div>}
    </Message>
      --> <div>example text</div>

  // HTML
    const message = {
      id: '...',
      defaultMessage: 'This <bold>message</bold> contains <cplink>markup</cplink> and {variables, number}.',
    };
    const values = {
      variables: 3,
      bold: chunks => <strong>{chunks}</strong>,
      cplink: chunks => <a href="http://www.classpass.com">{chunks}</a>,
    };
    <Message message={message} values={values} />
    --> <span>This <strong>message</strong> contains <a href="http://www.classpass.com">markup</and> and 3.</span>

  // DO NOT USE INTENTIONALLY: String fallback
    <Message message="example text" />

  // DEPRECATED: Message fields as individual props
    <Message id="..." defaultMessage="..." />

    <Message {...message} />

  // Lookup
    const messages = {
      foo: {...} // message obj
      bar: {...} // message obj
    };
    <Message id="foo" messages={messages} />

  // Dates - `helpers/intl-named-formats` for a list of formats
    <Message format="time" value={new Date()} />
      --> <span>4:05 AM</span>

    <Message format="long-w-dow-time" value={new Date()} />
      --> <span>Thursday, January 2, 2003, 4:05 AM</span>

  // Numbers - `helpers/intl-named-formats` for a list of formats
    <Message format="decimal" value={45.67} />
      --> <span>45.67</span>

    <Message format="percent" value={0.4567} />
      --> <span>46%</span>

    <Message format="currency" value={45.67} currency="USD" />
      --> <span>$45.67</span>

    <Message format="currency-short" value={45.67} currency="USD" />
      --> <span>$46</span>

*/

class Message extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    defaultMessage: PropTypes.string,
    description: PropTypes.string,
    message: PropTypes.oneOfType([intlMessage, PropTypes.string]),
    values: PropTypes.shape({}),
    messages: PropTypes.shape({}),
    format: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.number]),
    currency: PropTypes.string,
    as: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.elementType]),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    maximumFractionDigits: PropTypes.number,
    minimumFractionDigits: PropTypes.number,
  };

  static defaultProps = {
    as: 'span',
  };

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidMount() {
    const { id, defaultMessage } = this.props;

    if (id && defaultMessage) {
      reportWarning(`Message: individual props: ${id}`);
    }
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Translation error:', { error, info });
    }

    const { id, message } = this.props;
    const re = /\(created by (\w*)\)/g; // FYI: the `/g` prevents the `while` from exploding
    const matches = [];
    let match = re.exec(info.componentStack);
    let component;
    while (match) {
      [, component] = match;
      if (component !== 'Message' && !component.startsWith('Formatted')) {
        matches.push(`<${component}>`);
      }
      match = re.exec(info.componentStack);
    }
    const reportedMatch = matches[0] || '<Not Specified>';
    const reportedId = id || (message && message.id) || 'No id provided';
    reportWarning(`react-intl error`, {
      match: reportedMatch,
      id: reportedId,
      props: this.props,
    });
  }

  render() {
    const {
      id,
      defaultMessage,
      description,
      message,
      values,
      messages,
      format,
      value,
      currency,
      as: Component,
      children,
      maximumFractionDigits,
      minimumFractionDigits,
      ...rest
    } = this.props;
    const { hasError } = this.state;

    const translationId = id || (message && message.id) || String(value);

    const renderProp = nodes => {
      if (_isFunction(children)) {
        let kids = nodes;
        if (isFragment(nodes)) {
          kids = nodes.props.children;
        }
        return children(kids);
      }

      if (Component === 'text') {
        return React.createElement(Fragment, {}, nodes);
      }

      return React.createElement(Component, { ...rest, 'data-translation-id': translationId }, ...nodes);
    };

    if (hasError) {
      return renderProp(id || ' ');
    }

    if (id && defaultMessage) {
      return (
        <FormattedMessage id={id} defaultMessage={defaultMessage} description={description} values={values}>
          {renderProp}
        </FormattedMessage>
      );
    }

    if (isMessage(message)) {
      return (
        <FormattedMessage {...message} values={message.values || values}>
          {renderProp}
        </FormattedMessage>
      );
    }

    if (_isString(message)) {
      return renderProp(message);
    }

    if (id && messages) {
      return (
        <FormattedMessage {...messages[id]} values={values}>
          {renderProp}
        </FormattedMessage>
      );
    }

    if (_isDate(value)) {
      return (
        <FormattedDate format={format} value={value}>
          {renderProp}
        </FormattedDate>
      );
    }

    if (_isNumber(value)) {
      if (format === 'text') {
        return (
          <Component {...rest} data-translation-id={translationId}>
            {value}
          </Component>
        );
      }

      return (
        <FormattedNumber
          format={!format && !!currency ? 'currency' : format}
          currency={currency}
          value={value}
          {...(minimumFractionDigits && { minimumFractionDigits })}
          {...(maximumFractionDigits && { maximumFractionDigits })}>
          {renderProp}
        </FormattedNumber>
      );
    }

    // Something is wrong. Force an error and let the error boundary do its thing.
    return <FormattedMessage />;
  }
}

export default Message;
